"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
// Copyright © 2021 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.isOperator = isOperator;
exports._getOperator = _getOperator;
const analytics_1 = require("@hcengineering/analytics");
function $push(document, keyval) {
    const doc = document;
    for (const key in keyval) {
        if (doc[key] === undefined) {
            doc[key] = [];
        }
        const kvk = keyval[key];
        if (typeof kvk === 'object' && kvk != null) {
            const arr = doc[key];
            const desc = kvk;
            if ('$each' in desc) {
                if (arr != null && Array.isArray(arr)) {
                    arr.splice(desc.$position ?? 0, 0, ...desc.$each);
                }
            }
            else {
                arr.push(kvk);
            }
        }
        else {
            if (doc[key] === null || doc[key] === undefined) {
                doc[key] = [kvk];
            }
            else {
                if (Array.isArray(doc[key])) {
                    doc[key].push(kvk);
                }
                else {
                    analytics_1.Analytics.handleError(new Error(`invalid array value: ${JSON.stringify(doc[key])} `));
                    doc[key] = [kvk];
                }
            }
        }
    }
}
function $pull(document, keyval) {
    const doc = document;
    for (const key in keyval) {
        if (doc[key] === undefined) {
            doc[key] = [];
        }
        // Ensure doc[key] is an array before attempting to filter
        if (!Array.isArray(doc[key])) {
            analytics_1.Analytics.handleError(new Error(`$pull operation on non-array field: ${key}, value: ${JSON.stringify(doc[key])}`));
            doc[key] = [];
            continue;
        }
        const arr = doc[key];
        const kvk = keyval[key];
        if (typeof kvk === 'object' && kvk !== null) {
            const { $in } = kvk;
            doc[key] = (arr ?? []).filter((val) => {
                if ($in !== undefined) {
                    return !$in.includes(val);
                }
                else {
                    // We need to match all fields
                    for (const [kk, kv] of Object.entries(kvk)) {
                        if (val[kk] !== kv) {
                            return true;
                        }
                    }
                    return false;
                }
            });
        }
        else {
            doc[key] = (arr ?? []).filter((val) => val !== kvk);
        }
    }
}
function matchArrayElement(docs, query) {
    let result = [...docs];
    for (const key in query) {
        const value = query[key];
        const tresult = [];
        for (const object of result) {
            const val = object[key];
            if (val === value) {
                tresult.push(object);
            }
        }
        result = tresult;
        if (tresult.length === 0) {
            break;
        }
    }
    return result;
}
function $update(document, keyval) {
    const doc = document;
    for (const key in keyval) {
        if (doc[key] === undefined) {
            doc[key] = [];
        }
        // Ensure doc[key] is an array before attempting to update
        if (!Array.isArray(doc[key])) {
            analytics_1.Analytics.handleError(new Error(`$update operation on non-array field: ${key}, value: ${JSON.stringify(doc[key])}`));
            doc[key] = [];
            continue;
        }
        const val = keyval[key];
        if (typeof val === 'object') {
            const arr = doc[key];
            const desc = val;
            for (const m of matchArrayElement(arr, desc.$query)) {
                for (const [k, v] of Object.entries(desc.$update)) {
                    m[k] = v;
                }
            }
        }
    }
}
function $inc(document, keyval) {
    const doc = document;
    for (const key in keyval) {
        const cur = doc[key] ?? 0;
        // Ensure current value is a number
        if (typeof cur !== 'number' || isNaN(cur)) {
            analytics_1.Analytics.handleError(new Error(`$inc operation on non-numeric field: ${key}, value: ${JSON.stringify(doc[key])}`));
            doc[key] = keyval[key];
            continue;
        }
        const increment = keyval[key];
        // Ensure increment value is a valid number
        if (typeof increment !== 'number' || isNaN(increment)) {
            analytics_1.Analytics.handleError(new Error(`$inc operation with invalid increment: ${key}, increment: ${JSON.stringify(increment)}`));
            continue;
        }
        doc[key] = cur + increment;
    }
}
function $unset(document, keyval) {
    const doc = document;
    for (const key in keyval) {
        if (doc[key] !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete doc[key];
        }
    }
}
function $rename(document, keyval) {
    const doc = document;
    for (const key in keyval) {
        if (doc[key] !== undefined) {
            doc[keyval[key]] = doc[key];
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete doc[key];
        }
    }
}
const operators = {
    $push,
    $pull,
    $update,
    $inc,
    $unset,
    $rename
};
/**
 * @public
 */
function isOperator(o) {
    if (o === null || typeof o !== 'object') {
        return false;
    }
    const keys = Object.keys(o);
    return keys.length > 0 && keys.every((key) => key.startsWith('$'));
}
/**
 * @internal
 * @param name -
 * @returns
 */
function _getOperator(name) {
    const operator = operators[name];
    if (operator === undefined)
        throw new Error('unknown operator: ' + name);
    return operator;
}
//# sourceMappingURL=operator.js.map